import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ErrorFallback } from './utils/sentry';
import App from './App';

// Sentry Configuration
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development';
const APP_VERSION = import.meta.env.VITE_VERSION || '1.0.0';
const ENABLE_DEBUG_LOGS = import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';

// Initialize Sentry only if DSN is provided
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: `aqrate-portal@${APP_VERSION}`,

    // Environment-specific sampling rates
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.25 : 1.0,
    replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 0.5,
    replaysOnErrorSampleRate: ENVIRONMENT === 'production' ? 0.5 : 1.0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring
    enableTracing: true,

    // Debug mode for development
    debug: ENABLE_DEBUG_LOGS,

    // Send default PII data to Sentry (as requested by user)
    sendDefaultPii: true,

    // Custom options
    beforeSend(event, hint) {
      // Filter out errors in development if needed
      if (ENVIRONMENT === 'development' && !ENABLE_DEBUG_LOGS) {
        return null;
      }
      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Stale deployment cache — old chunk URLs 404 after a new deploy; handled in UI
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      // Google Identity Services SDK reads document.cookie internally; throws when the app is loaded
      // inside a sandboxed iframe missing allow-same-origin (AQRATE-PORTAL-1E) — not first-party code.
      "Failed to read the 'cookie' property from 'Document'",
    ],
  });
}

// Suppress ResizeObserver errors from CRA error overlay
// Harmless browser notifications from pdfme's internal resize handling
window.addEventListener('error', (event) => {
  if (event.message?.includes('ResizeObserver loop')) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);